import { Box } from '@primer/react';
import { AccessLevel, isEnoughAccessToReadWithoutWeights } from '@upright/common/iam/access/types';
import { ModelEntityType } from '@upright/common/model/input/types';
import { SdgDrillDownTableRow } from '@upright/common/model/output/scores/sdg-alignment/api-types';
import {
  SdgAlignmentByLevel,
  SdgAlignmentValence,
  SdgId,
  SdgTarget,
} from '@upright/common/model/output/scores/sdg-alignment/types';
import { useSdgReasonings } from '@upright/ui/api/model/output/scores/sdg-reasonings';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import {
  buildReasoningStateLookup,
  collectReasoningKeys,
} from '@upright/ui/common/upright/model/output/scores/sdg-alignments/reasonings/sdg-reasoning-keys';
import { SdgDrillDownTargetSelector } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/filters';
import { SdgDrillDownModalTable } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/table';
import { sortExplanation } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/table/sort';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  sdgId: SdgId;
  valence: SdgAlignmentValence;
  data: SdgAlignmentByLevel;
  rows: SdgDrillDownTableRow[] | undefined;
  entityType: ModelEntityType;
  hasProductViewAccess: boolean;
  accessLevel?: AccessLevel;
}

export function SdgDrilldownModalMain(props: Props) {
  const [target, setTarget] = useState<undefined | SdgTarget>(undefined);
  const isCompany = props.entityType === ModelEntityType.COMPANY;
  const canSeeAllItems = isEnoughAccessToReadWithoutWeights(props.accessLevel);

  const reasoningKeys = useMemo(
    () => collectReasoningKeys(props.rows ?? [], props.valence),
    [props.rows, props.valence],
  );
  const reasoningsResult = useSdgReasonings(reasoningKeys);
  const reasoningStateByKey = useMemo(
    () =>
      buildReasoningStateLookup(reasoningKeys, reasoningsResult.data?.results ?? [], {
        generationStopped: reasoningsResult.generationStopped,
      }),
    [reasoningKeys, reasoningsResult.data?.results, reasoningsResult.generationStopped],
  );
  return (
    <Box>
      <Box as="p" mb={3}>
        The table below lists {isCompany ? 'products' : 'constituents'} that contribute to the{' '}
        {props.valence === SdgAlignmentValence.ALIGNED ? 'alignment' : 'misalignment'} with SDG {props.sdgId}. Use the
        dropdown to filter the table to show only {isCompany ? 'products' : 'constituents'} that contribute to a given
        SDG target. {sortExplanation(isCompany, props.sdgId, props.valence)}.
      </Box>
      <SdgDrillDownTargetSelector
        entityType={props.entityType}
        selected={target}
        onChange={setTarget}
        data={props.data}
        valence={props.valence}
      />
      <Box mt={3}>
        {props.rows && (
          <SdgDrillDownModalTable
            hasProductViewAccess={props.hasProductViewAccess}
            entityType={props.entityType}
            rows={props.rows}
            sdg={props.sdgId}
            selectedTarget={target}
            selectedValence={props.valence}
            disablePaginationControls={!canSeeAllItems}
            reasoningStateByKey={reasoningStateByKey}
          />
        )}
        {!canSeeAllItems && (
          <Box sx={{ marginTop: '-1em', background: BouColor.PLATFORM_BG_GRAY_LIGHTER, padding: '1em' }}>
            Please <Link to="/signup">sign up</Link> for free or <Link to="/login">log in</Link> to your account to view
            the full drilldown.
          </Box>
        )}
      </Box>
    </Box>
  );
}
